import axios, { AxiosResponse } from 'axios';
import {
  BundleSKU,
  InventoryItem,
  StockValidationResult,
  BundleCreationRequest,
  BundleCreationResponse,
  APIResponse,
  RefreshStockResponse,
  InventoryReportResponse,
  BundleSearchResponse,
  BundleSearchAPIResponse,
  ShelfInventoryItem,
  ShelfInventoryResponse,
  SingleShelfInventoryAPIResponse,
  BundleSearchResult,
  BundleCategoryResponse,
  CardingShelfDataPage,
  CardingPackagingUsageEntry,
  CardingMoveLog,
  PackagingCatalogItem,
  PackagingPerSkuDiff,
  PackagingHistoryPage,
  DataHealthReport,
  ActionableRuleMeta,
  ActionableRuleConfig,
  ActionableItem,
  ActionablesMineResponse,
  ActionablesRunResult,
  ActionablesSummary,
  AccessControlUser,
  UserLoginHistory
} from '../types';
// (dev mode mock imports removed for production)

// Configuration
const API_BASE_URL = "/apis";
// const API_BASE_URL = "http://15.206.104.109:8080";
const N8N_BASE_URL = "https://tech.salty.co.in/webhook";

// Global axios interceptor for handling token expiration across all requests
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle token expiration — logout ONLY on 401 (expired/invalid/epoch-stale token).
    // A 403 (authenticated but under-privileged) is a normal RBAC outcome and MUST fall
    // through to the caller so route guards / usePermission can render an Unauthorized
    // panel or disabled controls instead of logging the user out (§0.1, review Finding 1).
    if (error.response && error.response.status === 401) {
      // Don't auto-logout in dev mode (backend has DEV_SKIP_AUTH so 401s are unrelated to our session)
      const isDevMode = localStorage.getItem('dev_mode') === 'true';
      if (isDevMode) {
        return Promise.reject(error);
      }

      // Check if this is an authentication endpoint to avoid infinite loops.
      // Google sign-in (401 for non-Salty/unverified) and access-request routes must NOT
      // auto-logout the user.
      //
      // /api/auth/me/permissions is the RBAC bootstrap call PermissionContext fires on every
      // load/refresh. A transient 401 there (e.g. a momentarily stale token right after a
      // TOKEN_EPOCH bump) must NOT force-logout + window.location.reload() the whole app —
      // that reload is what bounced a user off a deep route (/inventory, /carding, ...) on a
      // hard refresh. PermissionContext already fails CLOSED (empty permissions -> everything
      // hidden) with a non-blocking 15s retry for this endpoint, so letting the error fall
      // through to it is the correct, non-destructive handling. This is scoped to the
      // permissions bootstrap ONLY; a genuine 401 on any other (data/mutation) endpoint still
      // clears the session and reloads as before.
      const requestUrl: string = error.config?.url || '';
      const isAuthEndpoint =
        requestUrl.includes('/api/auth/login') ||
        requestUrl.includes('/api/auth/google') ||
        requestUrl.includes('/api/access-requests') ||
        requestUrl.includes('/api/auth/me/permissions');

      if (!isAuthEndpoint) {
        // Clear authentication data
        localStorage.removeItem('auth_token');
        localStorage.removeItem('bundleManagerUser');

        // Reload the page to redirect to login
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication
apiClient.interceptors.request.use((config) => {
  // Add auth token if available
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export class WarehouseAPI {
  // Bundle SKU Management
  static async getBundleSKUs(): Promise<BundleSKU[]> {
    const response: AxiosResponse<APIResponse<BundleSKU[]>> = await apiClient.get('/bundles');
    return response.data.data;
  }

  static async getBundleSKU(id: string): Promise<BundleSKU> {
    const response: AxiosResponse<APIResponse<BundleSKU>> = await apiClient.get(`/bundles/${id}`);
    return response.data.data;
  }

  // Inventory Management
  static async getInventoryReport(skuIds?: string[]): Promise<InventoryItem[]> {
    const params = skuIds ? { skuIds: skuIds.join(',') } : {};
    const response: AxiosResponse<APIResponse<InventoryReportResponse>> = await apiClient.get('/inventory', { params });
    return response.data.data.items;
  }

  static async getInventoryBySKU(skuId: string): Promise<InventoryItem> {
    const response: AxiosResponse<APIResponse<InventoryItem>> = await apiClient.get(`/inventory/${skuId}`);
    return response.data.data;
  }

  // Stock Validation
  static async validateStock(bundleSKUId: string, quantity: number): Promise<StockValidationResult> {
    const response: AxiosResponse<APIResponse<StockValidationResult>> = await apiClient.post('/inventory/validate-stock', {
      bundleSKUId,
      quantity,
    });
    return response.data.data;
  }

  // Bundle Creation Process
  static async deductChildSKUs(bundleSKUId: string, quantity: number, shelfLocations: Record<string, string>): Promise<boolean> {
    const response: AxiosResponse<APIResponse<boolean>> = await apiClient.post('/inventory/deduct-child-skus', {
      bundleSKUId,
      quantity,
      shelfLocations,
    });
    return response.data.data;
  }

  static async createBundle(request: BundleCreationRequest): Promise<BundleCreationResponse> {
    const response: AxiosResponse<APIResponse<BundleCreationResponse>> = await apiClient.post('/bundles/create', request);
    return response.data.data;
  }

  static async addBundleToInventory(bundleSKUId: string, quantity: number, shelfLocation: string): Promise<boolean> {
    const response: AxiosResponse<APIResponse<boolean>> = await apiClient.post('/inventory/add-bundle', {
      bundleSKUId,
      quantity,
      shelfLocation,
    });
    return response.data.data;
  }

  // Inventory Adjustments (Direct Unicommerce API calls)
  static async adjustInventory(skuId: string, quantity: number, operation: 'ADD' | 'SUBTRACT', shelfLocation?: string): Promise<boolean> {
    const response: AxiosResponse<APIResponse<boolean>> = await apiClient.post('/inventory/adjust', {
      skuId,
      quantity,
      operation,
      shelfLocation,
    });
    return response.data.data;
  }

  // Additional methods
  static async getRecentActivity(): Promise<any[]> {
    const response: AxiosResponse<APIResponse<any[]>> = await apiClient.get('/activity/recent');
    return response.data.data;
  }

  // Admin user management
  static async getUsers(): Promise<Array<{ email: string; name: string; role?: string; isAdmin: boolean }>> {
    const url = `${this.UNICOMMERCE_PROXY_BASE}/api/auth/users`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.get(url, { headers });
    return res.data.data || [];
  }

  static async updateUser(email: string, payload: { name?: string; role?: string }) {
    const url = `${this.UNICOMMERCE_PROXY_BASE}/api/auth/users/${encodeURIComponent(email)}`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.patch(url, payload, { headers });
    return res.data;
  }

  static async deleteUser(email: string) {
    const url = `${this.UNICOMMERCE_PROXY_BASE}/api/auth/users/${encodeURIComponent(email)}`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.delete(url, { headers });
    return res.data;
  }

  static async changePassword(currentPassword: string, newPassword: string) {
    const url = `${this.UNICOMMERCE_PROXY_BASE}/api/auth/change-password`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.post(url, { currentPassword, newPassword }, { headers });
    return res.data;
  }

  // RBAC: the caller's own resolved permissions (self-scoped, read-only). The map always
  // contains all 14 feature keys (missing -> "none").
  static async getMyPermissions(): Promise<{
    email: string;
    role: string | null;
    roleName: string | null;
    isAdmin: boolean;
    permissions: Record<string, 'none' | 'view' | 'edit'>;
  }> {
    const url = `${this.LOGGING_BASE}/api/auth/me/permissions`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.get(url, { headers });
    return res.data.data;
  }

  // RBAC: the 7 roles + their matrices (manage-users:view on the backend).
  static async getRoles(): Promise<Array<{
    key: string;
    name: string;
    permissions: Record<string, 'none' | 'view' | 'edit'>;
    isSystem: boolean;
    locked: boolean;
  }>> {
    const url = `${this.LOGGING_BASE}/api/roles`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.get(url, { headers });
    return res.data.data || [];
  }

  // RBAC matrix editor: replace a role's WHOLE permissions map (manage-users:edit on the
  // backend). `permissions` must carry all 14 feature keys; the backend refuses 'admin'
  // (locked) and invalidates its cache so getMyPermissions() reflects the change.
  static async updateRolePermissions(
    key: string,
    permissions: Record<string, 'none' | 'view' | 'edit'>,
  ): Promise<{
    key: string;
    name: string;
    permissions: Record<string, 'none' | 'view' | 'edit'>;
    isSystem: boolean;
    locked: boolean;
  }> {
    const url = `${this.LOGGING_BASE}/api/roles/${encodeURIComponent(key)}`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.put(url, { permissions }, { headers });
    return res.data.data;
  }

  // Access Control center (admin-only; access-control:edit on the backend). The user list +
  // each user's latest-login snapshot. Safe fields only — the backend never returns
  // passwordHash/googleId/tokens.
  static async getAccessControlUsers(): Promise<AccessControlUser[]> {
    const url = `${this.LOGGING_BASE}/api/access-control/users`;
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.get(url, { headers });
    return res.data.data || [];
  }

  // A single user's login history, newest first (access-control:edit on the backend). Safe
  // fields only. `userId` is encodeURIComponent'd into the path.
  static async getUserLoginHistory(userId: string): Promise<UserLoginHistory> {
    const url = `${this.LOGGING_BASE}/api/access-control/users/${encodeURIComponent(userId)}/logins`;
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.get(url, { headers });
    return res.data.data;
  }

  // Access requests (super-admin only on the backend). The response interceptor skips
  // /api/access-requests, so a 403 to a non-super-admin surfaces as an error instead of
  // force-logging-out the user.
  static async listAccessRequests(status: string = 'pending'): Promise<Array<{ _id: string; name: string; email: string; picture?: string | null; reason: string; status: string; requestedAt: string }>> {
    const url = `${this.UNICOMMERCE_PROXY_BASE}/api/access-requests`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.get(url, { headers, params: { status } });
    return res.data.data || [];
  }

  static async approveAccessRequest(id: string, role: string) {
    const url = `${this.UNICOMMERCE_PROXY_BASE}/api/access-requests/${encodeURIComponent(id)}/approve`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.post(url, { role }, { headers });
    return res.data;
  }

  static async rejectAccessRequest(id: string) {
    const url = `${this.UNICOMMERCE_PROXY_BASE}/api/access-requests/${encodeURIComponent(id)}/reject`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.post(url, {}, { headers });
    return res.data;
  }

  // Fetch recent logs from local backend logging service
  static async getRecentLogs(limit: number = 50, page: number = 1, type: string = 'all', q: string = ''): Promise<{ data: any[], page: number, totalPages: number, totalItems: number }> {
    const base = this.LOGGING_BASE;
    const token = localStorage.getItem('auth_token');
    const headers: any = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await axios.get(`${base}/api/logs/recent`, {
      params: { limit, page, type, q },
      headers
    });
    return response.data;
  }

  static async getDashboardStats(): Promise<any> {
    const response: AxiosResponse<APIResponse<any>> = await apiClient.get('/dashboard/stats');
    return response.data.data;
  }

  // Bundle Search API - Always uses real API endpoint
  // Note: Frontend implements 800ms debouncing to optimize API calls
  static async searchBundles(bundleSku: string, page: number = 0, limit: number = 10): Promise<BundleSearchResponse> {
    // Convert 0-indexed page to 1-indexed for API
    const apiPage = page + 1;

    // Real API call to the production endpoint
    const response: AxiosResponse<BundleSearchAPIResponse> = await axios.get(
      `${API_BASE_URL}/product-bundles?bundleSku=${bundleSku}&page=${apiPage}&limit=${limit}`,
      {
        headers: {
          'X-Correlation-ID': 'abcd-efgh-ijkl-mnop'
        }
      }
    );

    // Convert API response page back to 0-indexed for frontend consistency
    const apiResponse = response.data.data;
    return {
      ...apiResponse,
      page: apiResponse.page - 1 // Convert back to 0-indexed
    };
  }

  // Unicommerce Integration Methods
  // Every Unicommerce inventory-adjust call goes through this. The webhook is IP-blocked
  // from our server, so the browser must call it directly — but it first CLAIMS the
  // idempotency key from the backend, and reports the outcome afterwards. The backend's
  // unique index guarantees a given key is only ever allowed to send once, so a repeat of
  // the same logical adjustment (double invocation, retry after a partial failure) is
  // replayed or refused instead of being booked a second time. An outcome that is unknown
  // (timeout / 5xx / tab closed mid-call) is never auto-resent — it may have applied.
  //
  // Returns an axios-shaped response so existing `response.data...` handling keeps working.
  static async postInventoryAdjust(payload: any, idempotencyKey: string): Promise<AxiosResponse<any>> {
    const token = localStorage.getItem('auth_token');
    const guardHeaders: any = { 'Content-Type': 'application/json' };
    if (token) guardHeaders.Authorization = `Bearer ${token}`;
    const guardBase = `${this.LOGGING_BASE}/api/unicommerce/inventory-adjust`;

    const claim = await axios.post(`${guardBase}/claim`, { idempotencyKey, payload }, { headers: guardHeaders });
    if (claim.data?.action === 'replay') {
      return { data: claim.data.response, status: 200, statusText: 'OK (replayed)', headers: {}, config: {} } as AxiosResponse<any>;
    }

    // Reporting the outcome must never mask the real result. If it fails, the record stays
    // "pending" and later retries are refused as unknown — the safe direction.
    const complete = (outcome: 'done' | 'failed' | 'unknown', response?: any, error?: string) =>
      axios.post(`${guardBase}/complete`, { idempotencyKey, outcome, response, error }, { headers: guardHeaders })
        .catch(() => undefined);

    try {
      const response = await axios.post(`${N8N_BASE_URL}/inventory-adjust`, payload, {
        headers: { 'Content-Type': 'application/json', 'X-Correlation-ID': idempotencyKey.slice(0, 100) },
      });
      const entries = Array.isArray(response.data) ? response.data : [response.data];
      const results = entries.flatMap((e: any) => (Array.isArray(e?.inventoryAdjustmentResponses) ? e.inventoryAdjustmentResponses : []));
      const failed = results.filter((r: any) => r?.successful === false);
      if (failed.length > 0) await complete('failed', undefined, JSON.stringify(failed));
      else if (results.length === 0) await complete('unknown', undefined, 'n8n returned no adjustment result');
      else await complete('done', response.data);
      return response;
    } catch (err: any) {
      const status = err.response?.status;
      await complete(
        status && status >= 400 && status < 500 ? 'failed' : 'unknown',
        undefined,
        err.response?.data ? JSON.stringify(err.response.data) : err.message
      );
      throw err;
    }
  }

  // `idempotencyKey` must be stable for one logical adjustment (same creation, same shelf).
  // It is also reused (shortened) as the Unicommerce lot number, so if two rows ever appear
  // for one creation, identical lot numbers mean the duplication happened downstream of us.
  static async adjustBundleInventoryOnUnicommerce(bundleSKUCode: string, quantity: number, shelfLocation: string, idempotencyKey: string): Promise<any> {

    try {
      const payload = {
        sheetName: "Purchase", // Use Purchase sheet for adding inventory (not Sale sheet)
        rowNumber: Date.now() + Math.floor(Math.random() * 1000),
        data: {
          "SKU": bundleSKUCode,
          "Quantity": Math.abs(quantity), // Ensure positive quantity to ADD to inventory
          "Shelf Number": shelfLocation, // Use the user's selected shelf
          "Inventory Type": "GOOD",
          "Lot Number": `BUNDLE-${idempotencyKey.slice(0, 8)}`,
          "Channel": "BUNDLE_CREATION",
          "Remarks": `Bundle creation - adding ${Math.abs(quantity)} units of ${bundleSKUCode} to inventory at ${shelfLocation}`,
          "status": "Added"
        }
      };

      const response: AxiosResponse<any> = await this.postInventoryAdjust(payload, idempotencyKey);

      // Check for inner success status in inventoryAdjustmentResponses
      const adjustmentResponse = response.data?.inventoryAdjustmentResponses?.[0];
      if (adjustmentResponse && adjustmentResponse.successful === false) {
        const innerError = adjustmentResponse.errors?.[0];
        const innerMessage = innerError?.description || innerError?.message || 'Inner adjustment failed';
        throw new Error(innerMessage);
      }

      return {
        success: true,
        message: 'Bundle inventory updated successfully',
        data: response.data
      };

    } catch (error: any) {

      const responseData = error.response?.data;
      const errorMessage = responseData?.data || responseData?.message || error.message || 'Unknown error';

      // A genuine Unicommerce rejection is NOT a success. Previously these branches returned
      // success:true ("tracked locally") because the child-SKU deduction had already happened,
      // which masked real downstream failures (the bundle quantity never landed in Unicommerce)
      // and reported them to the user as a clean success. Surface them instead: the only caller
      // (BundleCreator) awaits this inside a try/catch and shows the thrown message as an error,
      // so throwing is what actually propagates the failure to the UI (a bare `success:false`
      // would be silently ignored by that caller and keep the masking).
      const status = error.response?.status;
      if (errorMessage.includes('INVENTORY_NOT_FOUND_AT_EXPECTED_LOCATION')) {
        // Bundle SKU not present in Unicommerce at the expected location — the adjustment did
        // NOT apply, so this is a failure the operator must see (not a silent local-only success).
        throw new Error(`Unicommerce could not adjust inventory: the bundle SKU "${bundleSKUCode}" was not found at ${shelfLocation}. The bundle was NOT added to Unicommerce inventory.`);
      }

      // Unicommerce tenant/access problems — the adjustment was rejected, not applied.
      if (errorMessage.includes('tenant is inactive') || errorMessage.includes('Illegal Access')) {
        throw new Error(`Unicommerce rejected the inventory adjustment (access/tenant issue): ${errorMessage}. The bundle was NOT added to Unicommerce inventory.`);
      }

      // Auth failures from Unicommerce — rejected, not applied.
      if (status === 403 || status === 401) {
        throw new Error(`Unicommerce rejected the inventory adjustment (${status} authorization error). The bundle was NOT added to Unicommerce inventory.`);
      }

      throw new Error(`Bundle inventory adjustment failed: ${errorMessage}`);
    }
  }

  // --- Backend API base URL ----------------------------------------------------------
  // Auto-detected so this can't be committed wrong: on localhost (CRA dev server),
  // hit the local backend directly; everywhere else (production), use same-origin
  // "" since every call site already prefixes its path with /api/ and Apache proxies
  // /api/* to the backend.
  static LOGGING_BASE = window.location.hostname === "localhost" ? "http://localhost:4000" : ""
  static UNICOMMERCE_PROXY_BASE = this.LOGGING_BASE;

  // Unicommerce status helper endpoints (proxy on backend)
  static async getUnicommerceStatus(): Promise<any> {
    const url = `${this.UNICOMMERCE_PROXY_BASE}/api/unicommerce/status`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.get(url, { headers });
    return res.data;
  }

  // Newest ShelfwiseInventory.updatedAt, read from THIS backend's Mongo (reachable in dev and
  // prod). The sync-completion poller and the "Inventory synced" freshness chip use this
  // instead of getShelfWiseInventory (which targets the prod-only /apis proxy and so can't
  // signal completion in local dev). Returns an ISO string or null.
  static async getInventoryFreshness(): Promise<string | null> {
    const url = `${this.UNICOMMERCE_PROXY_BASE}/api/unicommerce/inventory-freshness`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.get(url, { headers });
    return res.data?.updatedAt ?? null;
  }

  static async getDebundleSuggestions(): Promise<{ data: BundleSearchResult[] }> {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
    const response = await axios.get(`${this.LOGGING_BASE}/api/bundle-categories/suggestions`, { headers });
    return response.data;
  }

  // Bundle Combination / Smart Suggestions
  static async getBundleSuggestionFilters(): Promise<{ categories: string[]; subCategories: string[]; categorySubcategoryMap: Record<string, string[]> }> {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
    const response = await axios.get(`${this.LOGGING_BASE}/api/bundle-suggestions/filters`, { headers });
    return response.data.data;
  }

  static async getBundleSuggestions(filters: {
    slots?: { category: string; subCategory: string }[];
    minDRR?: number;
    minInventory?: number;
    limit?: number;
    comboSize?: number;
    excludeSkus?: string[];
  }): Promise<{ data: any[]; total: number }> {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
    const params: any = {};
    // Send each slot as individual flat params to avoid JSON URL-encoding issues
    if (filters.slots) {
      filters.slots.forEach((slot, i) => {
        params[`slot${i}_category`] = slot.category || '';
        params[`slot${i}_subCategory`] = slot.subCategory || '';
      });
    }
    if (filters.minDRR !== undefined) params.minDRR = filters.minDRR;
    if (filters.minInventory !== undefined) params.minInventory = filters.minInventory;
    if (filters.limit !== undefined) params.limit = filters.limit;
    if (filters.comboSize !== undefined) params.comboSize = filters.comboSize;
    if (filters.excludeSkus && filters.excludeSkus.length > 0) params.excludeSkus = filters.excludeSkus;
    const response = await axios.get(`${this.LOGGING_BASE}/api/bundle-suggestions`, { headers, params });
    return response.data;
  }

  // Search individual product items (child SKUs) — same data source as Bundle Combination
  static async searchProductItems(query: string, limit: number = 100): Promise<any[]> {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
    const params: any = { comboSize: 1, limit };
    if (query) params.q = query;
    const response = await axios.get(`${this.LOGGING_BASE}/api/bundle-suggestions`, { headers, params });
    return response.data?.data || [];
  }

  // Search SKUs from Redshift product_master_base via backend proxy
  static async searchSkusFromRedshift(query: string): Promise<any[]> {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
    const response = await axios.post(
      `${this.LOGGING_BASE}/api/image-generation/search-skus`,
      { query },
      { headers }
    );
    return response.data?.data || [];
  }



  static async analyzeBundle(components: {
    sku: string; name: string; imageUrl: string | null; category: string; subCategory: string;
  }[]): Promise<{ score: number; scoreLabel: string; bundleName: string; whyBundle: string; visualHarmony: string; occasion: string; tagline: string }> {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' };
    const response = await axios.post(`${this.LOGGING_BASE}/api/bundle-suggestions/ai-analyze`, { components }, { headers });
    return response.data.data;
  }

  static async generateBundlePreview(components: {
    sku: string; name: string; category: string; subCategory: string; imageUrl: string | null;
  }[]): Promise<{ imageUrl: string }> {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' };
    const response = await axios.post(`${this.LOGGING_BASE}/api/bundle-suggestions/preview-image`, { components }, { headers });
    return { imageUrl: response.data.imageUrl };
  }

  static async generateCustomImage(payload: {
    components: { sku: string; name: string; category: string; imageUrl: string | null }[];
    model: string;
    settings: {
      lighting?: string;
      angle?: string;
      style?: string;
      dimensions?: string;
      imageType?: string;
      shootSetup?: string;
      lens?: string;
      customPrompt?: string;
      [key: string]: any;
    };
    referenceImagesBase64?: string[];
    mode?: string;
    replacementBaseImage?: string | null;
  }): Promise<{ imageUrl: string; previewUrl?: string; s3Key?: string | null }> {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' };
    const response = await axios.post(`${this.LOGGING_BASE}/api/image-generation/generate`, payload, { headers });
    return {
      imageUrl: response.data.imageUrl,
      previewUrl: response.data.previewUrl,
      s3Key: response.data.s3Key || null,
    };
  }

  static async getImageGenerationLogs(limit = 20, page = 1): Promise<{ logs: any[]; total: number; page: number; limit: number }> {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
    const response = await axios.get(`${this.LOGGING_BASE}/api/image-generation/logs`, { headers, params: { limit, page } });
    return response.data;
  }

  static async deleteImageGenerationLog(logId: string): Promise<{ success: boolean; message?: string }> {
    const headers = { Authorization: `Bearer ${localStorage.getItem('auth_token')}` };
    const response = await axios.delete(`${this.LOGGING_BASE}/api/image-generation/logs/${logId}`, { headers });
    return response.data;
  }

  static async startUnicommerceStatus(payload: { jobCodes?: { shelfwise?: string; bundles?: string }; serverMessage?: string }) {
    const url = `${this.UNICOMMERCE_PROXY_BASE}/api/unicommerce/status/start`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.post(url, payload, { headers });
    return res.data;
  }

  static async finishUnicommerceStatus(payload?: { finishedAt?: string }) {
    const url = `${this.UNICOMMERCE_PROXY_BASE}/api/unicommerce/status/finish`;
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.post(url, payload || {}, { headers });
    return res.data;
  }

  static async logChildDeduction(payload: {
    sku: string;
    qty: number;
    bundleSku?: string;
    bundleQuantity?: number;
    shelfLocation?: string;
    metadata?: any;
  }) {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await axios.post(`${this.LOGGING_BASE}/api/logs/child-deductions`, payload, { headers });
      return res.data;
    } catch (err) {
      // don't block main flow if logging fails; just surface a warning
      console.warn('Failed to log child deduction', err);
      return null;
    }
  }

  static async logBundleCreation(payload: {
    bundleSku: string;
    quantity: number;
    childSkus?: Array<{ sku: string; qty: number }>;
    shelfLocation?: string;
    metadata?: any;
  }) {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await axios.post(`${this.LOGGING_BASE}/api/logs/bundles`, payload, { headers });
      return res.data;
    } catch (err) {
      console.warn('Failed to log bundle creation', err);
      return null;
    }
  }

  // Debundle Operations
  static async logDebundle(payload: {
    bundleSku: string;
    quantity: number;
    sourceShelfLocation?: string;
    childSkus?: Array<{ sku: string; qty: number }>;
    targetLocations?: Array<{ shelfLocation: string; skuCode: string; quantity: number }>;
    metadata?: any;
  }) {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await axios.post(`${this.LOGGING_BASE}/api/logs/debundles`, payload, { headers });
      return res.data;
    } catch (err) {
      console.warn('Failed to log debundle operation', err);
      return null;
    }
  }

  // Deduct bundle from Unicommerce inventory (uses Sale sheet - same as child SKU deduction)
  static async deductBundleInventoryOnUnicommerce(
    bundleSKUCode: string,
    allocations: Array<{ shelfCode: string; quantity: number }>,
    operationId: string
  ): Promise<any> {
    try {
      // Deduct from each shelf location separately
      const results = [];
      const errors = [];

      for (let idx = 0; idx < allocations.length; idx++) {
        const allocation = allocations[idx];
        if (allocation.quantity <= 0) continue;

        try {
          const payload = {
            sheetName: "Sale", // Use Sale sheet for removing from inventory
            rowNumber: Date.now() + Math.floor(Math.random() * 1000),
            data: {
              "SKU": bundleSKUCode,
              "Quantity": Math.abs(allocation.quantity),
              "Shelf Number": allocation.shelfCode,
              "Inventory Type": "GOOD",
              "Lot Number": `DEBUNDLE-${operationId.slice(0, 8)}-${allocation.shelfCode}`,
              "Channel": "DEBUNDLE",
              "Remarks": `Debundle - removing ${Math.abs(allocation.quantity)} units of ${bundleSKUCode} from inventory at ${allocation.shelfCode}`,
              "status": "Picked"
            }
          };

          const response: AxiosResponse<any> = await this.postInventoryAdjust(
            payload,
            `${operationId}:debundle-bundle:${idx}:${bundleSKUCode}:${allocation.shelfCode}:${allocation.quantity}`
          );

          // Check for inner success status in inventoryAdjustmentResponses
          const adjustmentResponse = response.data?.inventoryAdjustmentResponses?.[0];
          if (adjustmentResponse && adjustmentResponse.successful === false) {
            const innerError = adjustmentResponse.errors?.[0];
            const innerMessage = innerError?.description || innerError?.message || 'Inner adjustment failed';
            throw new Error(`${allocation.shelfCode}: ${innerMessage}`);
          }

          results.push({
            shelfCode: allocation.shelfCode,
            quantity: allocation.quantity,
            success: true
          });
        } catch (shelfError: any) {
          const responseData = shelfError.response?.data;
          const errorMessage = responseData?.data || responseData?.message || shelfError.message || 'Unknown error';
          errors.push({
            shelfCode: allocation.shelfCode,
            quantity: allocation.quantity,
            error: errorMessage
          });
        }
      }

      // If all deductions failed, throw an error
      if (errors.length > 0 && results.length === 0) {
        const errorDetails = errors.map(e => `${e.shelfCode}(${e.quantity}): ${e.error}`).join('; ');
        throw new Error(`All bundle deductions failed: ${errorDetails}`);
      }

      return {
        success: true,
        message: `Bundle inventory deducted successfully from ${results.length} shelf location(s)`,
        data: { results, errors }
      };
    } catch (error: any) {
      const responseData = error.response?.data;
      const errorMessage = responseData?.data || responseData?.message || error.message || 'Unknown error';

      // Handle errors gracefully similar to bundle creation
      if (error.response?.status === 403 || error.response?.status === 401) {
        return {
          success: true,
          message: 'Bundle deducted successfully (Unicommerce access restricted)',
          data: { note: 'Debundle recorded locally - Unicommerce access restricted' }
        };
      }

      // Handle INVALID_ITEM_TYPE error - occurs when bundle SKU doesn't exist in Unicommerce
      // This is expected for inactive bundles or bundles not yet synced to Unicommerce
      if (errorMessage.includes('INVALID_ITEM_TYPE')) {
        return {
          success: true,
          message: 'Bundle deducted successfully (not tracked in Unicommerce)',
          data: { note: 'Bundle SKU not found in Unicommerce - this is expected for inactive or legacy bundles' }
        };
      }

      // Handle inventory not found errors
      if (errorMessage.includes('INVENTORY_NOT_FOUND_AT_EXPECTED_LOCATION')) {
        return {
          success: true,
          message: 'Bundle deducted successfully (tracked locally)',
          data: { note: 'Bundle inventory not found at specified location in Unicommerce' }
        };
      }

      throw new Error(`Bundle inventory deduction failed: ${errorMessage}`);
    }
  }

  // Add child SKUs back to Unicommerce inventory (uses Purchase sheet - reverse of child SKU deduction)
  static async addChildSKUsToInventory(
    adjustments: Array<{
      skuCode: string;
      shelfCode: string;
      quantity: number;
    }>,
    operationId: string
  ): Promise<{
    success: boolean;
    message: string;
    adjustedItems?: number;
    successfulAdjustments?: Array<{ skuCode: string; shelfCode: string; quantity: number }>;
    errors?: string[];
  }> {
    try {
      let successCount = 0;
      const errors: string[] = [];
      const successfulAdjustments: Array<{ skuCode: string; shelfCode: string; quantity: number }> = [];

      for (let idx = 0; idx < adjustments.length; idx++) {
        const adjustment = adjustments[idx];
        try {
          const payload = {
            sheetName: "Purchase", // Use Purchase sheet for adding to inventory
            rowNumber: Date.now() + Math.floor(Math.random() * 1000),
            data: {
              "SKU": adjustment.skuCode,
              "Quantity": Math.abs(adjustment.quantity),
              "Shelf Number": adjustment.shelfCode,
              "Inventory Type": "GOOD",
              "Lot Number": `DEBUNDLE-CHILD-${operationId.slice(0, 8)}`,
              "Channel": "DEBUNDLE",
              "Remarks": `Debundle - returning ${Math.abs(adjustment.quantity)} units of ${adjustment.skuCode} to shelf ${adjustment.shelfCode}`,
              "status": "Added"
            }
          };

          const response = await this.postInventoryAdjust(
            payload,
            `${operationId}:add-child:${idx}:${adjustment.skuCode}:${adjustment.shelfCode}:${adjustment.quantity}`
          );

          // Check for inner success status
          const adjustmentResponse = response.data?.inventoryAdjustmentResponses?.[0];
          if (adjustmentResponse && adjustmentResponse.successful === false) {
            const innerError = adjustmentResponse.errors?.[0];
            const innerMessage = innerError?.description || innerError?.message || 'Inner adjustment failed';
            throw new Error(innerMessage);
          }

          successCount++;
          successfulAdjustments.push(adjustment);
        } catch (adjustmentError: any) {
          const responseData = adjustmentError.response?.data;
          let errorMsg = 'Unknown error';

          if (typeof responseData === 'string') {
            errorMsg = responseData;
          } else if (responseData?.data) {
            errorMsg = responseData.data;
          } else if (responseData?.message) {
            errorMsg = responseData.message;
          } else if (adjustmentError.message) {
            errorMsg = adjustmentError.message;
          }

          errors.push(`${adjustment.skuCode}: ${errorMsg}`);
        }
      }

      if (errors.length > 0) {
        // Return partial success info for rollback
        return {
          success: false,
          message: `Some adjustments failed: ${errors.join(', ')}`,
          adjustedItems: successCount,
          successfulAdjustments,
          errors
        };
      }

      return {
        success: true,
        message: `Successfully added ${successCount} child SKU items back to inventory`,
        adjustedItems: successCount,
        successfulAdjustments
      };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to add child SKUs to inventory';
      throw new Error(`Child SKU inventory addition failed: ${errorMessage}`);
    }
  }

  // Rollback bundle deduction by adding bundles back to inventory
  static async rollbackBundleDeduction(
    bundleSKUCode: string,
    allocations: Array<{ shelfCode: string; quantity: number }>,
    operationId: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const results = [];
      const errors = [];

      for (let idx = 0; idx < allocations.length; idx++) {
        const allocation = allocations[idx];
        if (allocation.quantity <= 0) continue;

        try {
          const payload = {
            sheetName: "Purchase", // Use Purchase sheet to add back
            rowNumber: Date.now() + Math.floor(Math.random() * 1000),
            data: {
              "SKU": bundleSKUCode,
              "Quantity": Math.abs(allocation.quantity),
              "Shelf Number": allocation.shelfCode,
              "Inventory Type": "GOOD",
              "Lot Number": `ROLLBACK-DEBUNDLE-${operationId.slice(0, 8)}-${allocation.shelfCode}`,
              "Channel": "DEBUNDLE_ROLLBACK",
              "Remarks": `Rollback: Adding back ${Math.abs(allocation.quantity)} units of ${bundleSKUCode} to ${allocation.shelfCode}`,
              "status": "Added"
            }
          };

          await this.postInventoryAdjust(
            payload,
            `${operationId}:rollback-bundle:${idx}:${bundleSKUCode}:${allocation.shelfCode}:${allocation.quantity}`
          );

          results.push({ shelfCode: allocation.shelfCode, quantity: allocation.quantity });
        } catch (err: any) {
          errors.push(`${allocation.shelfCode}: ${err.message}`);
        }
      }

      return {
        success: results.length > 0,
        message: results.length > 0
          ? `Rolled back ${results.length} bundle allocation(s)`
          : `Rollback failed: ${errors.join(', ')}`
      };
    } catch (error: any) {
      return {
        success: false,
        message: `Rollback failed: ${error.message}`
      };
    }
  }

  // Rollback child SKU additions by removing them from inventory
  static async rollbackChildSKUAdditions(
    adjustments: Array<{
      skuCode: string;
      shelfCode: string;
      quantity: number;
    }>,
    operationId: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const results = [];
      const errors = [];

      for (let idx = 0; idx < adjustments.length; idx++) {
        const adjustment = adjustments[idx];
        if (adjustment.quantity <= 0) continue;

        try {
          const payload = {
            sheetName: "Sale", // Use Sale sheet to remove
            rowNumber: Date.now() + Math.floor(Math.random() * 1000),
            data: {
              "SKU": adjustment.skuCode,
              "Quantity": Math.abs(adjustment.quantity),
              "Shelf Number": adjustment.shelfCode,
              "Inventory Type": "GOOD",
              "Lot Number": `ROLLBACK-CHILD-${operationId.slice(0, 8)}`,
              "Channel": "DEBUNDLE_ROLLBACK",
              "Remarks": `Rollback: Removing ${Math.abs(adjustment.quantity)} units of ${adjustment.skuCode} from ${adjustment.shelfCode}`,
              "status": "Picked"
            }
          };

          await this.postInventoryAdjust(
            payload,
            `${operationId}:rollback-child:${idx}:${adjustment.skuCode}:${adjustment.shelfCode}:${adjustment.quantity}`
          );

          results.push(adjustment);
        } catch (err: any) {
          errors.push(`${adjustment.skuCode}: ${err.message}`);
        }
      }

      return {
        success: results.length > 0,
        message: results.length > 0
          ? `Rolled back ${results.length} child SKU addition(s)`
          : `Rollback failed: ${errors.join(', ')}`
      };
    } catch (error: any) {
      return {
        success: false,
        message: `Rollback failed: ${error.message}`
      };
    }
  }

  static async refreshStockFromUnicommerce(): Promise<RefreshStockResponse> {
    try {
      // SHELFWISE refresh now goes through the backend direct UC export-job writer
      // (POST /api/unicommerce/sync-shelfwise), REPLACING the old wms-inventory n8n webhook.
      // The backend writer owns UnicommerceStatus (start/finish) itself — it does a GOOD-only,
      // TEST/NOT_FOUND-excluded, backed-up full replace of ShelfwiseInventory — so we do NOT
      // wrap it in the browser start/finish calls (that would double-manage isRunning).
      //
      // bundles + not-found are NOT yet covered by the direct sync (Phase B/C), so those two
      // n8n webhooks still fire for now. The old wms-inventory webhook is intentionally dropped.
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const bundlesUrl = 'https://tech.salty.co.in/webhook/wms-bundle';

      const [shelfwiseResult, bundlesResponse] = await Promise.all([
        // Direct shelfwise sync (backend) — now ALSO populates NotFoundInventory from the same
        // UC export pull (Phase B), so the old wms-inventory-notfound webhook is retired.
        axios.post(`${this.LOGGING_BASE}/api/unicommerce/sync-shelfwise`, {}, { headers }),
        // Bundle inventory still refreshes via n8n until Phase C folds it in.
        axios.get(bundlesUrl),
      ]);

      const extractResponseMessage = (resp: any, url: string) => {
        const d = resp?.data;
        if (typeof d === 'string') return d;
        return d?.job_code || d?.message || `webhook:${url}`;
      };

      const shelfwiseJobCode = `direct-sync: ${shelfwiseResult?.data?.insertedCount ?? '?'} shelf + ${shelfwiseResult?.data?.notFoundInsertedCount ?? '?'} not-found rows`;
      const bundlesJobCode = extractResponseMessage(bundlesResponse, bundlesUrl);
      const notfoundJobCode = 'direct-sync (folded into shelfwise)';

      return {
        success: true,
        message: 'Triggered inventory, bundles and not-found webhooks successfully',
        jobId: `Shelfwise: ${shelfwiseJobCode}, Bundles: ${bundlesJobCode}, NotFound: ${notfoundJobCode}`,
        shelfwiseJobCode,
        bundlesJobCode,
        syncedItems: undefined,
      };
    } catch (error: any) {
      try {
        await this.finishUnicommerceStatus({ finishedAt: new Date().toISOString() });
      } catch (_) { }
      const errorMessage = error.response?.data?.message || error.message || 'Failed to trigger webhooks for stock refresh';
      throw new Error(`Webhook trigger failed: ${errorMessage}`);
    }
  }

  // Shelf-wise Inventory API - Get inventory by SKU with pagination
  static async getShelfWiseInventory(
    skuCode: string,
    page: number = 0,
    limit: number = 10
  ): Promise<ShelfInventoryResponse> {
    try {
      // Convert 0-indexed page to 1-indexed for API
      const apiPage = page + 1;

      const apiUrl = `${API_BASE_URL}/shelfwise-inventory?page=${apiPage}&limit=${limit}&skuCode=${skuCode}`;

      // Real API call to the production endpoint
      const response: AxiosResponse<ShelfInventoryResponse> = await axios.get(
        apiUrl,
        {
          headers: {
            'X-Correlation-ID': 'abcd-efgh-ijkl-mnop'
          }
        }
      );

      // Direct API response structure: {data: [...], total, page, limit}
      if (response.data && Array.isArray(response.data.data)) {
        return {
          data: response.data.data,
          total: response.data.total,
          page: response.data.page > 0 ? response.data.page - 1 : 0, // Convert back to 0-indexed
          limit: response.data.limit
        };
      } else {
        return {
          data: [],
          total: 0,
          page: 0,
          limit: 1000
        };
      }
    } catch (error) {
      throw error;
    }
  }

  // Shelf-wise Inventory API - Get specific inventory item by ID
  static async getShelfInventoryById(inventoryId: string): Promise<ShelfInventoryItem> {
    // Real API call to the production endpoint
    const response: AxiosResponse<SingleShelfInventoryAPIResponse> = await axios.get(
      `${API_BASE_URL}/shelfwise-inventory/${inventoryId}`,
      {
        headers: {
          'X-Correlation-ID': 'abcd-efgh-ijkl-mnop'
        }
      }
    );

    return response.data.data;
  }

  // Unicommerce Inventory Adjustment API - Adjust inventory on Unicommerce platform
  static async adjustInventoryOnUnicommerce(
    adjustments: Array<{
      skuCode: string;
      shelfCode: string;
      quantity: number;
      inventoryType: string;
      operation: string;
    }>,
    operationId: string
  ): Promise<{ success: boolean; message: string; adjustedItems?: number }> {
    try {

      // Send each adjustment as a separate API call (matching Google Apps Script pattern)
      let successCount = 0;
      const errors: string[] = [];

      for (let idx = 0; idx < adjustments.length; idx++) {
        const adjustment = adjustments[idx];
        try {
          const isAdd = adjustment.operation === 'ADD';
          const payload = {
            sheetName: isAdd ? "Purchase" : "Sale",
            rowNumber: Date.now() + Math.floor(Math.random() * 1000),
            data: {
              "SKU": adjustment.skuCode,
              "Quantity": Math.abs(adjustment.quantity), // Integer quantity
              "Shelf Number": adjustment.shelfCode,
              "Inventory Type": adjustment.inventoryType === 'GOOD_INVENTORY' ? 'GOOD' : 'BAD',
              "Lot Number": isAdd ? `REVERSE-PICK-${operationId.slice(0, 8)}` : `LOT-${operationId.slice(0, 8)}`,
              "Channel": isAdd ? "DEBUNDLE" : "BUNDLE_CREATION", // Use DEBUNDLE for additions as it's proven to work
              "Remarks": isAdd
                ? `Bundle creation reversal - returning ${Math.abs(adjustment.quantity)} units to shelf ${adjustment.shelfCode}`
                : `Bundle picking - ${adjustment.operation} operation`,
              "status": isAdd ? "Added" : "Picked" // This triggers the Google Apps Script logic
            }
          };

          const response = await this.postInventoryAdjust(
            payload,
            `${operationId}:pick-adjust:${idx}:${adjustment.skuCode}:${adjustment.shelfCode}:${adjustment.quantity}:${adjustment.operation}`
          );

          // Check for inner success status
          const adjustmentResponse = response.data?.inventoryAdjustmentResponses?.[0];
          if (adjustmentResponse && adjustmentResponse.successful === false) {
            const innerError = adjustmentResponse.errors?.[0];
            const innerMessage = innerError?.description || innerError?.message || 'Inner adjustment failed';
            throw new Error(innerMessage);
          }

          successCount++;
        } catch (adjustmentError: any) {

          // Extract the most detailed error information available
          const responseData = adjustmentError.response?.data;
          let errorMsg = 'Unknown error';

          if (typeof responseData === 'string') {
            errorMsg = responseData;
          } else if (responseData?.data) {
            errorMsg = responseData.data;
          } else if (responseData?.message) {
            errorMsg = responseData.message;
          } else if (adjustmentError.message) {
            errorMsg = adjustmentError.message;
          }

          errors.push(`${adjustment.skuCode}: ${errorMsg}`);
        }
      }

      if (errors.length > 0) {
        throw new Error(`Some adjustments failed: ${errors.join(', ')}`);
      }

      // Return a standardized response
      return {
        success: true,
        message: `Successfully adjusted ${successCount} inventory items on Unicommerce`,
        adjustedItems: successCount
      };
    } catch (error: any) {

      const errorMessage = error.response?.data?.message || error.message || 'Failed to adjust inventory on Unicommerce';
      throw new Error(`Inventory adjustment failed: ${errorMessage}`);
    }
  }
  /**
   * Log a generic bundle category action
   */
  static async logBundleCategoryAction(action: string, details: any): Promise<void> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      await axios.post(`${this.LOGGING_BASE}/api/bundle-categories/log-action`, { action, details }, { headers });
    } catch (error) {
      console.warn('Failed to log bundle category action:', error);
    }
  }

  // ==================== Bundle Category Management ====================

  /**
   * Get bundle categories with their active bundle summary with pagination and search
   */
  static async getBundleCategories(
    page: number = 0,
    limit: number = 12,
    search: string = "",
    sortBy: string = "name-asc",
    filters: any = {}
  ): Promise<BundleCategoryResponse> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      // Convert 0-indexed page to 1-indexed for API
      const apiPage = page + 1;

      const params = new URLSearchParams({
        page: apiPage.toString(),
        limit: limit.toString(),
        search: search,
        sortBy: sortBy,
        ...filters,
      });

      const response = await axios.get(
        `${this.LOGGING_BASE}/api/bundle-categories?${params.toString()}`,
        { headers }
      );

      const apiResponse = response.data.data;
      return {
        data: apiResponse.data || [],
        total: apiResponse.total || 0,
        page: apiResponse.page > 0 ? apiResponse.page - 1 : 0, // Convert back to 0-indexed
        limit: apiResponse.limit || limit
      };
    } catch (error: any) {
      console.error('Failed to fetch bundle categories:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch bundle categories');
    }
  }

  /**
   * Get all versions of a specific parent SKU
   */
  /**
   * Single source of truth for the next version number — backed by the exact same
   * BundleCategory.getNextVersionNumber the actual create-version write path uses.
   * Never compute this independently client-side; that divergence is what caused the
   * PROPOSEBOX-RW class of bug (Unicommerce synced one version number, Mongo saved
   * another).
   */
  static async getNextVersionNumber(parentSKU: string): Promise<{ nextVersion: number; bundleSku: string }> {
    const normalizedParentSKU = parentSKU.toUpperCase().trim();
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await axios.get(
      `${this.LOGGING_BASE}/api/bundle-categories/${normalizedParentSKU}/next-version`,
      { headers }
    );
    return { nextVersion: response.data.nextVersion, bundleSku: response.data.bundleSku };
  }

  static async getBundleCategoryVersions(parentSKU: string): Promise<any[]> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.get(
        `${this.LOGGING_BASE}/api/bundle-categories/${parentSKU}/versions`,
        { headers }
      );
      return response.data.data || [];
    } catch (error: any) {
      console.error('Failed to fetch category versions:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch category versions');
    }
  }

  /**
   * Helper to sync bundle to Unicommerce from client-side browser
   */
  static async syncBundleToUnicommerceClientSide(bundle: any): Promise<boolean> {
    try {
      const payload: any = {
        categoryCode: bundle.categoryCode || "BUNDLE",
        skuCode: bundle.bundleSku,
        name: bundle.name || bundle.bundleSku,
        type: bundle.type || "SIMPLE",
        maxRetailPrice: bundle.maxRetailPrice || 0,
        basePrice: bundle.basePrice || 0,
        costPrice: bundle.costPrice || 0,
        gstTaxTypeCode: bundle.gstTaxTypeCode || "3",
        imageUrl: bundle.imageURL || "",
        brand: bundle.brand || "",
      };

      // If type is BUNDLE, add componentItemTypes and taxCalculationType as per requirement
      if (bundle.type === "BUNDLE") {
        payload.taxCalculationType = "PRICE_OF_BUNDLE_SKU";
        payload.componentItemTypes = (bundle.childs || []).map((childSku: string) => ({
          itemSku: childSku,
          quantity: 1,
          price: bundle.basePrice || 0,
        }));
      }

      const response = await axios.post(`${N8N_BASE_URL}/create-item`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-ID': `bundle-create-${bundle.bundleSku}-${Date.now()}`,
        }
      });

      // Check for inner success status inside Unicommerce response array/object
      const responseData = response.data;
      if (Array.isArray(responseData)) {
        const failedItem = responseData.find(item => item && item.successful === false);
        if (failedItem) {
          const errorMsg = failedItem.errors?.[0]?.description || failedItem.errors?.[0]?.message || 'Unicommerce item creation failed';
          throw new Error(errorMsg);
        }
      } else if (responseData && responseData.successful === false) {
        const errorMsg = responseData.errors?.[0]?.description || responseData.errors?.[0]?.message || 'Unicommerce item creation failed';
        throw new Error(errorMsg);
      }

      return true;
    } catch (webhookErr: any) {
      console.error(
        `Failed to call client-side create-item webhook for ${bundle.bundleSku}:`,
        webhookErr.message || webhookErr
      );
      throw webhookErr;
    }
  }

  /**
   * Create a new bundle category (first version)
   */
  static async createBundleCategory(payload: {
    parentSKU: string;
    childs: string[];
    imageURL?: string | null;
    categoryCode?: string | null;
    name?: string | null;
    type?: string | null;
    maxRetailPrice?: number | null;
    basePrice?: number | null;
    brand?: string | null;
    category?: string | null;
    subCategory?: string | null;
    handle?: string | null;
    tags?: string[] | null;
    plytix_id?: string | null;
    costPrice?: number | null;
    gstTaxTypeCode?: string | null;
    saleType: string;
    saleTag: string;
  }): Promise<any> {
    try {
      const normalizedParentSKU = payload.parentSKU.toUpperCase().trim();
      const bundleSku = `${normalizedParentSKU}-1`;

      // 1. Sync to Unicommerce first via client-side webhook
      const mockBundle = {
        parentSKU: normalizedParentSKU,
        bundleSku,
        versionNumber: 1,
        childs: payload.childs,
        categoryCode: payload.categoryCode,
        name: payload.name,
        type: payload.type || "BUNDLE",
        maxRetailPrice: payload.maxRetailPrice,
        basePrice: payload.basePrice,
        costPrice: payload.costPrice,
        gstTaxTypeCode: payload.gstTaxTypeCode,
        imageURL: payload.imageURL,
        brand: payload.brand,
      };

      await this.syncBundleToUnicommerceClientSide(mockBundle);

      // 2. If webhook is successful, only then persist in MongoDB
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.post(
        `${this.LOGGING_BASE}/api/bundle-categories`,
        payload,
        { headers }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to create bundle category:', error);
      throw new Error(error.message || error.response?.data?.error || 'Failed to create bundle category');
    }
  }

  /**
   * Create a new version of an existing category
   */
  static async createNewBundleVersion(parentSKU: string, payload: {
    childs: string[];
    isFuture?: boolean;
    imageURL?: string | null;
    categoryCode?: string | null;
    name?: string | null;
    type?: string | null;
    maxRetailPrice?: number | null;
    basePrice?: number | null;
    brand?: string | null;
    category?: string | null;
    tags?: string[] | null;
    plytix_id?: string | null;
    costPrice?: number | null;
    gstTaxTypeCode?: string | null;
    saleType?: string;
    saleTag?: string;
  }): Promise<any> {
    try {
      const normalizedParentSKU = parentSKU.toUpperCase().trim();

      // 1. Fetch the authoritative next version number (single source of truth — see
      // getNextVersionNumber) and the version history for data inheritance.
      const [{ nextVersion, bundleSku }, versions] = await Promise.all([
        this.getNextVersionNumber(normalizedParentSKU),
        this.getBundleCategoryVersions(normalizedParentSKU),
      ]);

      const latestVersion = versions[0] || {};

      // 2. Sync to Unicommerce first via client-side webhook
      const mockBundle = {
        parentSKU: normalizedParentSKU,
        bundleSku,
        versionNumber: nextVersion,
        childs: payload.childs,
        categoryCode: payload.categoryCode !== undefined ? payload.categoryCode : (latestVersion.categoryCode || null),
        name: payload.name !== undefined ? payload.name : (latestVersion.name || null),
        type: payload.type !== undefined ? payload.type : (latestVersion.type || "BUNDLE"),
        maxRetailPrice: payload.maxRetailPrice !== undefined ? payload.maxRetailPrice : (latestVersion.maxRetailPrice || 0),
        basePrice: payload.basePrice !== undefined ? payload.basePrice : (latestVersion.basePrice || 0),
        costPrice: payload.costPrice !== undefined ? payload.costPrice : (latestVersion.costPrice || 0),
        gstTaxTypeCode: payload.gstTaxTypeCode !== undefined ? payload.gstTaxTypeCode : (latestVersion.gstTaxTypeCode || "3"),
        imageURL: payload.imageURL !== undefined ? payload.imageURL : (latestVersion.imageURL || null),
        brand: payload.brand !== undefined ? payload.brand : (latestVersion.brand || null),
      };

      await this.syncBundleToUnicommerceClientSide(mockBundle);

      // 3. If webhook is successful, only then persist in MongoDB
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.post(
        `${this.LOGGING_BASE}/api/bundle-categories/${normalizedParentSKU}/new-version`,
        payload,
        { headers }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to create new version:', error);
      throw new Error(error.message || error.response?.data?.error || 'Failed to create new version');
    }
  }

  /**
   * Disable an active bundle
   */
  static async disableBundleCategory(id: string): Promise<any> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.put(
        `${this.LOGGING_BASE}/api/bundle-categories/${id}/disable`,
        {},
        { headers }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to disable bundle:', error);
      throw new Error(error.response?.data?.error || 'Failed to disable bundle');
    }
  }

  /**
   * Enable a disabled bundle
   */
  static async enableBundleCategory(id: string): Promise<any> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.put(
        `${this.LOGGING_BASE}/api/bundle-categories/${id}/enable`,
        {},
        { headers }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to enable bundle:', error);
      throw new Error(error.response?.data?.error || 'Failed to enable bundle');
    }
  }

  /**
   * Delete a disabled bundle
   */
  static async deleteBundleCategory(id: string): Promise<void> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      await axios.delete(
        `${this.LOGGING_BASE}/api/bundle-categories/${id}`,
        { headers }
      );
    } catch (error: any) {
      console.error('Failed to delete bundle:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete bundle');
    }
  }

  /**
   * Update a bundle category's name and/or imageURL
   */
  static async updateBundleCategory(parentSKU: string, payload: {
    newParentSKU?: string;
    imageURL?: string | null;
    categoryCode?: string | null;
    name?: string | null;
    type?: string | null;
    maxRetailPrice?: number | null;
    basePrice?: number | null;
    brand?: string | null;
    category?: string | null;
    subCategory?: string | null;
    handle?: string | null;
    tags?: string[] | null;
    plytix_id?: string | null;
    costPrice?: number | null;
    gstTaxTypeCode?: string | null;
    saleType?: string;
    saleTag?: string;
  }): Promise<any> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.put(
        `${this.LOGGING_BASE}/api/bundle-categories/${parentSKU}/update`,
        payload,
        { headers }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to update bundle category:', error);
      throw new Error(error.response?.data?.error || 'Failed to update bundle category');
    }
  }

  /**
   * Toggle the needsReorder flag on a bundle
   */
  static async toggleBundleReorder(id: string): Promise<{ id: string; needsReorder: boolean }> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.put(
        `${this.LOGGING_BASE}/api/bundle-categories/${id}/toggle-reorder`,
        {},
        { headers }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to toggle reorder:', error);
      throw new Error(error.response?.data?.error || 'Failed to toggle reorder');
    }
  }

  /**
   * Set the Sale Tag for whole parents (every version of each parent follows it).
   * `previous` maps each parent to its prior tag (null if it was unset/mixed) for undo.
   */
  static async setParentSaleTag(
    parentSKUs: string[],
    saleTag: 'Live' | 'Sunset'
  ): Promise<{ saleTag: string; updatedParents: number; updatedVersions: number; previous: Record<string, 'Live' | 'Sunset' | null>; notFound: string[] }> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.put(
        `${this.LOGGING_BASE}/api/bundle-categories/parent-sale-tag`,
        { parentSKUs, saleTag },
        { headers }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to set parent sale tag:', error);
      throw new Error(error.response?.data?.error || 'Failed to update Sale Tag');
    }
  }

  /**
   * Update a single bundle version's imageURL
   */
  static async updateBundleVersionImage(id: string, imageURL?: string, plytix_id?: string, name?: string): Promise<any> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.put(
        `${this.LOGGING_BASE}/api/bundle-categories/${id}/update-image`,
        { imageURL, plytix_id, name },
        { headers }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to update version:', error);
      throw new Error(error.response?.data?.error || 'Failed to update version');
    }
  }

  /**
   * Update full details of a bundle version
   */
  static async updateBundleFullDetails(id: string, updates: any): Promise<any> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.put(
        `${this.LOGGING_BASE}/api/bundle-categories/${id}/full-update`,
        updates,
        { headers }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to update bundle details:', error);
      throw new Error(error.response?.data?.error || 'Failed to update bundle details');
    }
  }

  /**
   * Get distinct filter values (brand, category, subCategory, tags) for the
   * GB/Bundle View filter dropdowns, sourced directly from the DB.
   */
  static async getBundleCategoryFilterValues(): Promise<{
    brands: string[];
    categories: string[];
    subCategories: string[];
    tags: string[];
  }> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.get(
        `${this.LOGGING_BASE}/api/bundle-categories/filter-values`,
        { headers }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch filter values:', error);
      return { brands: [], categories: [], subCategories: [], tags: [] };
    }
  }

  // ==================== Actionables ====================

  private static authHeaders2(): any {
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }

  static async getActionableRules(): Promise<ActionableRuleMeta[]> {
    const r = await axios.get(`${this.LOGGING_BASE}/api/actionables/rules`, { headers: this.authHeaders2() });
    return r.data.data;
  }

  /** Open count for the header badge: everyone's for super-admins (scope "all"), own items otherwise. */
  static async getMyActionableCount(): Promise<{ open: number; scope: 'all' | 'mine' }> {
    const r = await axios.get(`${this.LOGGING_BASE}/api/actionables/mine/count`, { headers: this.authHeaders2() });
    return r.data.data;
  }

  static async getMyActionables(params: { status: 'open' | 'closed'; rule?: string; page?: number; limit?: number }): Promise<ActionablesMineResponse> {
    const r = await axios.get(`${this.LOGGING_BASE}/api/actionables/mine`, { headers: this.authHeaders2(), params });
    return r.data.data;
  }

  /** Marks done only if the rule's live check passes; otherwise `done` is false and `message` says why. */
  static async markActionableDone(id: string): Promise<{ done: boolean; message: string; item: ActionableItem }> {
    const r = await axios.post(`${this.LOGGING_BASE}/api/actionables/${id}/done`, {}, { headers: this.authHeaders2() });
    return r.data;
  }

  /**
   * Admin actionables list. `params` is forwarded straight to axios (undefined values omitted), so
   * supported query keys are: status, rule, role (a single role key — the Team filter), assignee,
   * from, to, q, page, limit. `role` narrows to items whose assignedRoles contain it and AND-combines
   * with the other filters server-side.
   */
  static async adminGetActionables(params: Record<string, any>): Promise<{ items: ActionableItem[]; total: number; page: number; limit: number }> {
    const r = await axios.get(`${this.LOGGING_BASE}/api/actionables/admin/items`, { headers: this.authHeaders2(), params });
    return r.data.data;
  }

  static async adminGetActionablesSummary(): Promise<ActionablesSummary> {
    const r = await axios.get(`${this.LOGGING_BASE}/api/actionables/admin/summary`, { headers: this.authHeaders2() });
    return r.data.data;
  }

  static async adminAssignActionable(id: string, email: string | null): Promise<ActionableItem> {
    const r = await axios.put(`${this.LOGGING_BASE}/api/actionables/admin/items/${id}/assign`, { email }, { headers: this.authHeaders2() });
    return r.data.data;
  }

  static async adminDismissActionable(id: string, reason: string): Promise<ActionableItem> {
    const r = await axios.post(`${this.LOGGING_BASE}/api/actionables/admin/items/${id}/dismiss`, { reason }, { headers: this.authHeaders2() });
    return r.data.data;
  }

  static async adminRunActionables(dryRun: boolean): Promise<ActionablesRunResult> {
    const r = await axios.post(`${this.LOGGING_BASE}/api/actionables/admin/run`, { dryRun }, { headers: this.authHeaders2(), timeout: 120000 });
    return r.data.data;
  }

  static async adminGetActionableRules(): Promise<ActionableRuleConfig[]> {
    const r = await axios.get(`${this.LOGGING_BASE}/api/actionables/admin/rules`, { headers: this.authHeaders2() });
    return r.data.data;
  }

  static async adminSaveActionableRule(key: string, body: { enabled?: boolean; assigneeEmails?: string[]; assigneeRoles?: string[]; params?: Record<string, number> }): Promise<void> {
    await axios.put(`${this.LOGGING_BASE}/api/actionables/admin/rules/${key}`, body, { headers: this.authHeaders2() });
  }

  static async adminGetActionableUsers(): Promise<{ email: string; name: string; role: string }[]> {
    const r = await axios.get(`${this.LOGGING_BASE}/api/actionables/admin/users`, { headers: this.authHeaders2() });
    return r.data.data;
  }

  /**
   * Read-only comparison of every active bundle against Plytix (source of truth)
   * and Unicommerce. Never writes to either system.
   */
  static async getDataHealthCheck(): Promise<DataHealthReport> {
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await axios.get(
      `${this.LOGGING_BASE}/api/bundle-categories/data-health`,
      { headers }
    );
    return response.data.data;
  }

  /**
   * Lightweight, read-only dashboard counts. Hits the fast
   * /api/bundle-categories/dashboard-stats endpoint (does NOT run the expensive
   * data-health check). Legacy getDashboardStats() is intentionally left untouched.
   */
  static async getDashboardStats2(): Promise<{ activeBundles: number; needReorder: number; openDrafts: number }> {
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await axios.get(
      `${this.LOGGING_BASE}/api/bundle-categories/dashboard-stats`,
      { headers }
    );
    return response.data.data;
  }

  /**
   * Pulls Unicommerce's full product catalogue into the local mirror the Data
   * Health check reads from. Admin-only. Takes ~30s (pages through ~16k SKUs) —
   * the health check itself is otherwise comparing against whatever was synced
   * last, which can silently go stale.
   */
  static async syncUnicommerceProducts(): Promise<{ totalRecords: number; fetched: number; uniqueSkus: number }> {
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await axios.post(
      `${this.LOGGING_BASE}/api/unicommerce/sync-products`,
      {},
      { headers }
    );
    return response.data;
  }

  /**
   * Distinct {categoryCode, categoryName} pairs from Unicommerce — Unicommerce is
   * the source of truth for Category Code, so this backs a pick-only dropdown
   * instead of free text.
   */
  static async getUnicommerceCategoryCodes(): Promise<Array<{ categoryCode: string; categoryName: string | null }>> {
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await axios.get(
      `${this.LOGGING_BASE}/api/unicommerce/category-codes`,
      { headers }
    );
    return response.data.data;
  }

  static async searchPlytixSkus(query: string): Promise<Array<{
    sku: string;
    name: string;
    category: string | null;
    subCategory: string | null;
    brand: string | null;
    handle: string | null;
    tags: string[];
    costPrice: number | null;
    basePrice: number | null;
    maxRetailPrice: number | null;
    plytixId: string | null;
    images: string[];
  }>> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.get(
        `${this.LOGGING_BASE}/api/bundle-categories/plytix-sku-search`,
        { headers, params: { q: query } }
      );
      return response.data.data || [];
    } catch (error: any) {
      console.error('Failed to search Plytix SKUs:', error);
      return [];
    }
  }

  static async suggestParentName(
    mode: 'Simple' | 'Bundle',
    components: Array<{ sku: string; name: string; category: string | null }>
  ): Promise<{ parentSKU: string; name: string } | null> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.post(
        `${this.LOGGING_BASE}/api/bundle-categories/suggest-parent-name`,
        { mode, components },
        { headers }
      );
      return response.data.data || null;
    } catch (error: any) {
      console.error('Failed to get AI name suggestion:', error);
      throw new Error(error.response?.data?.error || 'Failed to get AI name suggestion');
    }
  }

  static async getComponentCategoryOptions(): Promise<string[]> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.get(
        `${this.LOGGING_BASE}/api/bundle-categories/component-category-options`,
        { headers }
      );
      return response.data.data || [];
    } catch (error: any) {
      console.error('Failed to fetch component category options:', error);
      return [];
    }
  }

  private static authHeaders(): any {
    const token = localStorage.getItem('auth_token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }

  /**
   * Get per-SKU-per-shelf breakdown across all NONCARDED-* shelves, joined with cached
   * Plytix product info (image, linked packaging SKU(s), packaging image(s)).
   */
  static async getLastCardingSyncTime(): Promise<string | null> {
    const res = await axios.get(
      `${this.LOGGING_BASE}/api/carding/last-sync`,
      { headers: this.authHeaders() }
    );
    return res.data.success ? res.data.lastSyncedAt : null;
  }

  /**
   * Carding main grid, SERVER-PAGINATED — one page of rows at a time. Search/shelf/packaging
   * filters and sort are applied server-side; `total` is the count of ALL matching rows (for
   * the count caption + pager), not the page length. The per-row shape (CardingShelfRow) is
   * unchanged. `page` is 0-indexed.
   */
  static async getCardingShelfData(
    params: {
      page?: number;
      pageSize?: number;
      search?: string;
      shelf?: string;
      packaging?: string;
      sort?: string;
      dir?: 'asc' | 'desc';
    } = {}
  ): Promise<CardingShelfDataPage> {
    const { page = 0, pageSize = 50, search = '', shelf = 'all', packaging = 'all', sort, dir } = params;
    const query: Record<string, string | number> = { page, pageSize };
    if (search.trim()) query.q = search.trim();
    if (shelf && shelf !== 'all') query.shelf = shelf;
    if (packaging && packaging !== 'all') query.packaging = packaging;
    if (sort) query.sort = sort;
    if (dir) query.dir = dir;
    const response = await axios.get(
      `${this.LOGGING_BASE}/api/carding/shelf-data`,
      // Raised to 30s as a last-resort safety net — with chunked (paged) loading each
      // request is small and should return well under it, so this is no longer a tripwire
      // that hard-fails the whole tab on a slow full load (which no longer happens).
      { headers: this.authHeaders(), params: query, timeout: 30000 }
    );
    const body = response.data;
    return {
      data: Array.isArray(body.data) ? body.data : [],
      total: body.total ?? 0,
      page: body.page ?? page,
      pageSize: body.pageSize ?? pageSize,
    };
  }

  /**
   * All real shelf codes known to Unicommerce (sourced from the synced ShelfwiseInventory
   * collection) — used to populate the move dialog's shelf pickers live, not hardcoded.
   */
  static async getCardingShelfOptions(): Promise<string[]> {
    const response = await axios.get(
      `${this.LOGGING_BASE}/api/carding/shelf-options`,
      { headers: this.authHeaders() }
    );
    return response.data.data;
  }

  /**
   * Trigger a Plytix sync (image + packaging SKU linkage) for the given SKUs.
   * Capped server-side per call to respect Plytix's rate limit.
   */
  static async syncCardingPlytix(skus: string[]): Promise<any> {
    const response = await axios.post(
      `${this.LOGGING_BASE}/api/carding/sync-plytix`,
      { skus },
      { headers: this.authHeaders() }
    );
    return response.data;
  }

  /**
   * On-demand trigger for the server-side background Plytix/Redshift sync job —
   * runs the same job the cron schedule runs periodically, immediately.
   */
  static async runCardingSyncNow(): Promise<{ success: boolean; synced?: number; skipped?: number; message?: string }> {
    const response = await axios.post(
      `${this.LOGGING_BASE}/api/carding/sync-plytix/run-now`,
      {},
      { headers: this.authHeaders() }
    );
    return response.data;
  }

  /**
   * Phase 1: initiate a carding move (NONCARDED shelf -> target shelf). Builds the
   * Unicommerce inventory-adjust payload and logs it as "pending" — does not touch
   * Unicommerce or local inventory yet. The caller must POST the returned n8nPayload
   * to the Unicommerce webhook directly (server-side calls are IP-blocked), then call
   * confirmCardingMove/failCardingMove with the result.
   */
  static async initiateCardingMove(params: {
    skuCode: string;
    fromShelf: string;
    toShelf: string;
    quantity: number;
    packagingShelf?: string | null;
  }): Promise<{ success: boolean; requiresClientExecution: boolean; moveId: string; n8nPayload: any; correlationId: string }> {
    const response = await axios.post(
      `${this.LOGGING_BASE}/api/carding/move`,
      params,
      { headers: this.authHeaders() }
    );
    return response.data;
  }

  /**
   * Phase 1 for a damage move: relocates a SKU straight to DAMAGE (GOOD_INVENTORY ->
   * BAD_INVENTORY). Packaging is never touched. Same 2-phase confirm/fail flow as
   * initiateCardingMove — use confirmCardingMove/failCardingMove with the returned moveId.
   */
  static async initiateDamageMove(params: {
    skuCode: string;
    fromShelf: string;
    quantity: number;
  }): Promise<{ success: boolean; requiresClientExecution: boolean; moveId: string; toShelf: string; n8nPayload: any; correlationId: string }> {
    const response = await axios.post(
      `${this.LOGGING_BASE}/api/carding/damage`,
      params,
      { headers: this.authHeaders() }
    );
    return response.data;
  }

  /**
   * Which DAMAGE-family shelf (DAMAGE, DAMAGE-01, ...) a damage move for this SKU would
   * actually target — whichever already holds the most of that SKU's damaged stock.
   */
  static async getDamageShelfSuggestion(skuCode: string): Promise<{ success: boolean; shelfCode: string }> {
    const response = await axios.get(
      `${this.LOGGING_BASE}/api/carding/damage-shelf-suggestion/${encodeURIComponent(skuCode)}`,
      { headers: this.authHeaders() }
    );
    return response.data;
  }

  /**
   * Phase 2a: confirm a carding move after Unicommerce accepted the adjustment —
   * applies the local ShelfwiseInventory changes and marks the move "synced".
   */
  static async confirmCardingMove(moveId: string, unicommerceResponse: any): Promise<any> {
    const response = await axios.post(
      `${this.LOGGING_BASE}/api/carding/move/${moveId}/confirm`,
      { unicommerceResponse },
      { headers: this.authHeaders() }
    );
    return response.data;
  }

  /**
   * Phase 2b: mark a carding move as failed — Unicommerce rejected/errored, local
   * inventory is left untouched.
   */
  static async failCardingMove(moveId: string, reason: string, rawResponse?: any): Promise<any> {
    const response = await axios.post(
      `${this.LOGGING_BASE}/api/carding/move/${moveId}/fail`,
      { reason, rawResponse },
      { headers: this.authHeaders() }
    );
    return response.data;
  }

  /**
   * Reverse index: for each packaging SKU, which SKUs use it and how many.
   * Sourced entirely from the local Plytix cache — no live Plytix calls.
   */
  static async getCardingPackagingUsage(): Promise<CardingPackagingUsageEntry[]> {
    const response = await axios.get(
      `${this.LOGGING_BASE}/api/carding/packaging-usage`,
      { headers: this.authHeaders() }
    );
    return response.data.data;
  }

  /**
   * Recent carding move history — who moved what, from where to where, and the
   * exact Unicommerce remarks generated for the transfer.
   */
  static async getCardingMoves(): Promise<CardingMoveLog[]> {
    const response = await axios.get(
      `${this.LOGGING_BASE}/api/carding/moves`,
      { headers: this.authHeaders() }
    );
    return response.data.data;
  }

  // ── Packaging SKU mapping (manual, catalog-validated) ────────────────────────────
  /**
   * List the synced packaging catalog (Plytix product_type_new='packaging'), optionally
   * filtered by a sku substring. Backs the Edit-packaging dialog's multiselect options.
   */
  static async getPackagingCatalog(q?: string): Promise<PackagingCatalogItem[]> {
    const response = await axios.get(
      `${this.LOGGING_BASE}/api/packaging-mapping/catalog`,
      { headers: this.authHeaders(), params: q ? { q } : {} }
    );
    return response.data.data;
  }

  /**
   * Resolve the current packaging mapping for one SKU — a manual override wins, else the
   * Plytix-derived set. Returns the resolved packagingSkus plus their source.
   */
  static async getSkuPackagingMapping(sku: string): Promise<{ success: boolean; packagingSkus: string[]; source: 'manual' | 'plytix' | 'none' }> {
    const response = await axios.get(
      `${this.LOGGING_BASE}/api/packaging-mapping/mapping/${encodeURIComponent(sku)}`,
      { headers: this.authHeaders() }
    );
    return response.data;
  }

  /**
   * Save a manual packaging mapping for one SKU. The backend validates every packaging SKU
   * against the catalog and rejects the whole request if any is invalid; on success it
   * returns the added/removed/unchanged diff.
   */
  static async putSkuPackagingMapping(sku: string, packagingSkus: string[]): Promise<PackagingPerSkuDiff> {
    const response = await axios.put(
      `${this.LOGGING_BASE}/api/packaging-mapping/mapping/${encodeURIComponent(sku)}`,
      { packagingSkus },
      { headers: this.authHeaders() }
    );
    return response.data.diff;
  }

  /**
   * Two-phase bulk import. With dryRun:true the backend computes the per-SKU diff with no
   * writes; with dryRun:false it applies each valid set and records history. Pass either a
   * raw CSV string or pre-parsed rows.
   */
  static async bulkImportPackagingMappings(params: {
    rows?: { sku: string; packagingSkus: string[] }[];
    csv?: string;
    dryRun: boolean;
  }): Promise<{ success: boolean; dryRun: boolean; results: PackagingPerSkuDiff[] }> {
    const response = await axios.post(
      `${this.LOGGING_BASE}/api/packaging-mapping/bulk-import`,
      params,
      { headers: this.authHeaders() }
    );
    return response.data;
  }

  /**
   * Admin-gated full re-sync of the packaging catalog from Plytix. Returns the synced count.
   */
  static async syncPackagingCatalog(): Promise<{ success: boolean; count: number }> {
    const response = await axios.post(
      `${this.LOGGING_BASE}/api/packaging-mapping/sync-catalog`,
      {},
      { headers: this.authHeaders() }
    );
    return response.data;
  }

  /**
   * Export ALL SkuPackagingMap entries (not filtered) as a CSV blob — columns SKU,
   * Packaging SKUs, Created At (IST), Updated At (IST). The SKU + Packaging SKUs columns
   * round-trip cleanly back through the bulk-import parser.
   */
  static async exportPackagingMappings(): Promise<Blob> {
    const response = await axios.get(
      `${this.LOGGING_BASE}/api/packaging-mapping/export`,
      { headers: this.authHeaders(), responseType: 'blob' }
    );
    return response.data;
  }

  /**
   * Per-SKU packaging-mapping changelog, SERVER-PAGINATED — SKUs with at least one history
   * entry, newest SKU first, returned one page at a time. The optional `search` filters
   * server-side over the SKU code and packaging SKUs. Returns the page of SKUs plus `total`
   * (count of ALL matching SKUs, for the chip + pager). Backs the Carding History tab.
   */
  static async getPackagingMappingHistory(
    params: { page?: number; pageSize?: number; search?: string; sortBy?: string; sortDir?: 'asc' | 'desc' } = {}
  ): Promise<PackagingHistoryPage> {
    const { page = 0, pageSize = 20, search = '', sortBy, sortDir } = params;
    const query: Record<string, string | number> = { page, pageSize };
    if (search.trim()) query.q = search.trim();
    if (sortBy) query.sortBy = sortBy;
    if (sortDir) query.sortDir = sortDir;
    const response = await axios.get(
      `${this.LOGGING_BASE}/api/packaging-mapping/history`,
      { headers: this.authHeaders(), params: query }
    );
    const body = response.data;
    return {
      data: body.data,
      total: body.total,
      page: body.page,
      pageSize: body.pageSize,
    };
  }

  /**
   * Get component view — one row per unique child SKU across all active bundles.
   */
  static async getComponentView(
    page: number = 0,
    limit: number = 0,
    search: string = "",
    sortBy: string = "reorderQty-high",
    filters: any = {}
  ): Promise<{ success: boolean; data: import('../types').ComponentViewResponse }> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (search) params.append('search', search);
      if (sortBy) params.append('sortBy', sortBy);
      // Append non-default filter values
      if (filters.isPackagingFilter && filters.isPackagingFilter !== 'all') params.append('isPackagingFilter', filters.isPackagingFilter);
      if (filters.componentTypeFilter && filters.componentTypeFilter !== 'all') params.append('componentTypeFilter', filters.componentTypeFilter);
      if (filters.productCategoryFilter && filters.productCategoryFilter !== 'all') params.append('productCategoryFilter', filters.productCategoryFilter);
      if (filters.usedInGbFilter && filters.usedInGbFilter !== 'all') params.append('usedInGbFilter', filters.usedInGbFilter);
      if (filters.usedInBundleFilter && filters.usedInBundleFilter !== 'all') params.append('usedInBundleFilter', filters.usedInBundleFilter);
      if (filters.docRangeFilter && filters.docRangeFilter !== 'all') params.append('docRangeFilter', filters.docRangeFilter);
      if (filters.docMin !== undefined && filters.docMin !== '') params.append('docMin', String(filters.docMin));
      if (filters.docMax !== undefined && filters.docMax !== '') params.append('docMax', String(filters.docMax));
      if (filters.docNone) params.append('docNone', 'true');
      if (filters.skipImageEnrich) params.append('skipImageEnrich', 'true');

      const response = await axios.get(
        `${this.LOGGING_BASE}/api/bundle-categories/component-view?${params.toString()}`,
        { headers }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch component view:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch component view');
    }
  }

  /**
   * Search bundle categories by query (bundleSku, parentSKU, name, categoryCode)
   * Returns data compatible with BundleSearchResponse format
   */
  static async searchBundleCategories(
    query: string,
    page: number = 0,
    limit: number = 10,
    includeInactive: boolean = false,
    includeCogs: boolean = false
  ): Promise<BundleSearchResponse> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      // Convert 0-indexed page to 1-indexed for API
      const apiPage = page + 1;

      // includeCogs is opt-in (defaults off) so existing callers are unaffected; only the
      // Bundle Categories CSV export sets it, to carry per-version COGS in the response.
      const cogsParam = includeCogs ? `&includeCogs=true` : '';
      const response = await axios.get(
        `${this.LOGGING_BASE}/api/bundle-categories/search?q=${encodeURIComponent(query)}&page=${apiPage}&limit=${limit}&includeInactive=${includeInactive}${cogsParam}`,
        { headers }
      );

      const apiResponse = response.data.data;
      return {
        data: apiResponse.data || [],
        total: apiResponse.total || 0,
        page: apiResponse.page > 0 ? apiResponse.page - 1 : 0, // Convert back to 0-indexed
        limit: apiResponse.limit || limit
      };
    } catch (error: any) {
      console.error('Failed to search bundle categories:', error);
      throw new Error(error.response?.data?.error || 'Failed to search bundle categories');
    }
  }

  /**
   * Bulk upload bundle categories from CSV data
   */
  static async bulkUploadBundleCategories(bundles: Array<{
    parentSKU: string;
    childs: string[];
    isFuture?: boolean;
    categoryCode?: string;
    name?: string;
    type?: string;
    maxRetailPrice?: number;
    basePrice?: number;
    imageURL?: string;
    tags?: string[];
    plytix_id?: string;
  }>): Promise<{
    message: string;
    results: {
      created: Array<{ parentSKU: string; bundleSku: string }>;
      newVersions: Array<{ parentSKU: string; bundleSku: string; version: number }>;
      errors: Array<{ row: number; parentSKU?: string; error: string }>;
    };
  }> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.post(
        `${this.LOGGING_BASE}/api/bundle-categories/bulk-upload`,
        { bundles },
        { headers }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk upload:', error);
      throw new Error(error.response?.data?.error || 'Failed to bulk upload bundle categories');
    }
  }

  /**
   * Raw bulk upload for backfilling - allows full control over all fields
   */
  static async bulkUploadBundleCategoriesRaw(bundles: Array<{
    parentSKU: string;
    bundleSku?: string;
    versionNumber?: number;
    childs: string[];
    isFuture?: boolean;
    imageURL?: string;
    isActive?: boolean;
    disabledAt?: string | null;
    categoryCode?: string;
    name?: string;
    type?: string;
    maxRetailPrice?: number;
    basePrice?: number;
    tags?: string[];
    plytix_id?: string;
  }>, syncToExternal: boolean = false): Promise<{
    message: string;
    results: {
      inserted: Array<{ row: number; parentSKU: string; bundleSku: string; versionNumber: number; isActive: boolean }>;
      skipped: Array<{ row: number; bundleSku: string; reason: string }>;
      errors: Array<{ row: number; parentSKU?: string; bundleSku?: string; error: string }>;
    };
  }> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.post(
        `${this.LOGGING_BASE}/api/bundle-categories/bulk-upload-raw`,
        { bundles, syncToExternal },
        { headers }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to raw bulk upload:', error);
      throw new Error(error.response?.data?.error || 'Failed to bulk upload bundle categories');
    }
  }
  /**
   * Draft Management API
   */
  static async getDrafts(): Promise<any[]> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.get(`${this.LOGGING_BASE}/api/drafts`, { headers });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch drafts:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch drafts');
    }
  }

  static async getDraftById(id: string): Promise<any> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.get(`${this.LOGGING_BASE}/api/drafts/${id}`, { headers });
      return response.data;
    } catch (error: any) {
      console.error(`Failed to fetch draft ${id}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to fetch draft');
    }
  }

  static async createDraft(draftData: any): Promise<any> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.post(`${this.LOGGING_BASE}/api/drafts`, draftData, { headers });
      return response.data;
    } catch (error: any) {
      console.error('Failed to create draft:', error);
      throw new Error(error.response?.data?.error || 'Failed to create draft');
    }
  }

  static async updateDraft(id: string, draftData: any): Promise<any> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.patch(`${this.LOGGING_BASE}/api/drafts/${id}`, draftData, { headers });
      return response.data;
    } catch (error: any) {
      console.error(`Failed to update draft ${id}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to update draft');
    }
  }

  static async deleteDraft(id: string): Promise<any> {
    try {
      const token = localStorage.getItem('auth_token');
      const headers: any = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await axios.delete(`${this.LOGGING_BASE}/api/drafts/${id}`, { headers });
      return response.data;
    } catch (error: any) {
      console.error(`Failed to delete draft ${id}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to delete draft');
    }
  }

  // ── Cyclic Count ───────────────────────────────────────────────────────────

  static async createCyclicCount(refNumber: string): Promise<any> {
    const headers: any = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('auth_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.post(`${this.LOGGING_BASE}/api/cyclic-counts`, { refNumber }, { headers });
    return res.data.data;
  }

  static async listCyclicCounts(limit: number = 20): Promise<any[]> {
    const headers: any = {};
    const token = localStorage.getItem('auth_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.get(`${this.LOGGING_BASE}/api/cyclic-counts`, { headers, params: { limit } });
    return res.data.data;
  }

  static async getCyclicCount(id: string): Promise<any> {
    const headers: any = {};
    const token = localStorage.getItem('auth_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.get(`${this.LOGGING_BASE}/api/cyclic-counts/${id}`, { headers });
    return res.data.data;
  }

  static async addShelfToCyclicCount(id: string, shelfCode: string): Promise<any> {
    const headers: any = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('auth_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.post(`${this.LOGGING_BASE}/api/cyclic-counts/${id}/shelves`, { shelfCode }, { headers });
    return res.data.data;
  }

  static async removeShelfFromCyclicCount(id: string, shelfCode: string): Promise<any> {
    const headers: any = {};
    const token = localStorage.getItem('auth_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.delete(`${this.LOGGING_BASE}/api/cyclic-counts/${id}/shelves/${shelfCode}`, { headers });
    return res.data.data;
  }

  static async submitCyclicCountItems(id: string, counted: { shelfCode: string; skuCode: string; countedQty: number }[]): Promise<any> {
    const headers: any = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('auth_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.put(`${this.LOGGING_BASE}/api/cyclic-counts/${id}/counted`, { counted }, { headers });
    return res.data.data;
  }

  static async recordCyclicCountAdjustments(
    id: string,
    results: { adjustmentId: string; success: boolean; unicommerceResponse?: any }[]
  ): Promise<any> {
    const headers: any = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('auth_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.put(`${this.LOGGING_BASE}/api/cyclic-counts/${id}/adjust`, { results }, { headers });
    return res.data.data;
  }

  static async updateCyclicCountStatus(id: string, status: string): Promise<any> {
    const headers: any = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('auth_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.patch(`${this.LOGGING_BASE}/api/cyclic-counts/${id}`, { status }, { headers });
    return res.data.data;
  }

  /**
   * Get total GOOD_INVENTORY for a SKU from the local backend's ShelfwiseInventory collection
   * (same data source as the Bundle View — NOT the production Unicommerce API)
   */
  static async getLocalBundleSKUInventory(skuCode: string): Promise<{ skuCode: string; total: number }> {
    const headers: any = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('auth_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.get(`${this.LOGGING_BASE}/api/bundle-categories/inventory/${encodeURIComponent(skuCode)}`, { headers });
    return res.data.data;
  }

  /**
   * Calculate max possible bundles from child SKU inventory using the local backend
   * (same data source as the Bundle View — NOT the production Unicommerce API)
   */
  static async calculateLocalCanBeMade(childSkus: Array<{ skuId: string; requiredQuantity: number }>): Promise<{ canBeMade: number; inventoryMap: Record<string, number> }> {
    const headers: any = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('auth_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.post(`${this.LOGGING_BASE}/api/bundle-categories/inventory/can-be-made`, { childSkus }, { headers });
    return res.data.data;
  }

  static async getExcludedShelves(): Promise<string[]> {
    const headers: any = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('auth_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.get(`${this.LOGGING_BASE}/api/system-settings/excluded-shelves`, { headers });
    return res.data.data;
  }

  static async updateExcludedShelves(shelves: string[]): Promise<string[]> {
    const headers: any = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('auth_token');
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.post(`${this.LOGGING_BASE}/api/system-settings/excluded-shelves`, { value: shelves }, { headers });
    return res.data.data;
  }
}

export default WarehouseAPI;